import React, { useEffect, useState } from 'react';
import PatientChat from '../components/PatientChat';
import ClinicianDashboard from '../components/ClinicianDashboard';
import SymptomTracker from './SymptomTracker';
import MedicationTracker from './MedicationTracker';
import ClinicalTrials from './ClinicalTrials';
import DrugMonographs from './DrugMonographs';
import EquipmentLendingLibrary from './EquipmentLendingLibrary';
import PeerSupport from '../components/PeerSupport';

export default function Home({ user, userProfile, onEditProfile, onLogout }) {
  const [activeView, setActiveView] = useState(() => {
    const saved = localStorage.getItem('activeView');
    return saved || 'lending'; // Default to lending library
  });
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const isClinician = userProfile?.role === 'medical_professional';

  // Developer mode and feature toggles
  const [isDeveloperMode, setIsDeveloperMode] = useState(() => {
    return localStorage.getItem('developerMode') === 'true';
  });
  const [patientChatEnabled, setPatientChatEnabled] = useState(() => {
    const saved = localStorage.getItem('patientChatEnabled');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [drugMonographsEnabled, setDrugMonographsEnabled] = useState(() => {
    const saved = localStorage.getItem('drugMonographsEnabled');
    return saved !== null ? JSON.parse(saved) : false;
  });

  // Save feature toggles to localStorage
  useEffect(() => {
    localStorage.setItem('patientChatEnabled', JSON.stringify(patientChatEnabled));
  }, [patientChatEnabled]);

  useEffect(() => {
    localStorage.setItem('drugMonographsEnabled', JSON.stringify(drugMonographsEnabled));
  }, [drugMonographsEnabled]);

  useEffect(() => {
    localStorage.setItem('developerMode', isDeveloperMode.toString());
  }, [isDeveloperMode]);

  // Developer mode click counter using useRef (no re-render needed)
  const logoClickCountRef = React.useRef(0);

  // Save activeView to localStorage
  useEffect(() => {
    localStorage.setItem('activeView', activeView);
  }, [activeView]);

  // Effect to handle disabled tabs
  useEffect(() => {
    if (!patientChatEnabled && activeView === 'patient') {
      setActiveView('symptoms');
    }
    if (!drugMonographsEnabled && activeView === 'monographs') {
      setActiveView('symptoms');
    }
    if (!isDeveloperMode && activeView === 'clinician') {
      setActiveView('symptoms');
    }
  }, [patientChatEnabled, drugMonographsEnabled, isDeveloperMode, activeView]);

  // Keyboard shortcut for developer mode (Ctrl+Shift+D)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'D') {
        event.preventDefault();
        setIsDeveloperMode(!isDeveloperMode);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDeveloperMode]);

  // Logo click handler for developer mode (click 5 times)
  const handleLogoClick = () => {
    logoClickCountRef.current += 1;
    if (logoClickCountRef.current >= 5) {
      setIsDeveloperMode(!isDeveloperMode);
      logoClickCountRef.current = 0; // Reset counter
    }
  };

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Chatbot integration: symptom context is stored in localStorage under
  // 'symptomContext' so PatientChat (or any future AI component) can read it.
  const handleOpenChat = () => {
    setActiveView('patient');
  };

  const tabButtonStyle = (tabKey) => ({
    border: activeView === tabKey ? '1px solid #1D9E75' : '1px solid #d1d5db',
    background: activeView === tabKey
      ? 'linear-gradient(180deg, #ecfdf5 0%, #d1fae5 100%)'
      : 'white',
    color: activeView === tabKey ? '#065f46' : '#374151',
    borderRadius: '10px',
    padding: '9px 12px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: activeView === tabKey ? '700' : '500',
    boxShadow: activeView === tabKey
      ? '0 4px 14px rgba(16, 185, 129, 0.18)'
      : '0 2px 6px rgba(15, 23, 42, 0.05)',
    transition: 'all 0.18s ease',
  });

  const renderPatientOnlyMessage = (sectionName) => (
    <div
      style={{
        background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid #dbeafe',
        borderRadius: '16px',
        padding: '30px 24px',
        maxWidth: '720px',
        margin: '0 auto',
        textAlign: 'center',
        boxShadow: '0 16px 34px rgba(15, 23, 42, 0.08)',
      }}
    >
      <div
        style={{
          width: '64px',
          height: '64px',
          margin: '0 auto 12px',
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #dcfce7 0%, #dbeafe 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#065f46',
          fontWeight: '800',
          fontSize: '20px',
          letterSpacing: '0.4px',
        }}
      >
        FYI
      </div>
      <h2 style={{ margin: 0, fontSize: '22px', color: '#111827' }}>
        Not Applicable for Clinician Accounts
      </h2>
      <p style={{ margin: '10px 0 0', fontSize: '14px', color: '#64748b', lineHeight: 1.6 }}>
        The {sectionName} section is for patients. Please use the Clinician View tab for clinician workflows.
      </p>
    </div>
  );

  const renderClinicianOnlyMessage = () => (
    <div
      style={{
        background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid #dbeafe',
        borderRadius: '16px',
        padding: '30px 24px',
        maxWidth: '720px',
        margin: '0 auto',
        textAlign: 'center',
        boxShadow: '0 16px 34px rgba(15, 23, 42, 0.08)',
      }}
    >
      <div
        style={{
          width: '64px',
          height: '64px',
          margin: '0 auto 12px',
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #dcfce7 0%, #dbeafe 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#065f46',
          fontWeight: '800',
          fontSize: '20px',
          letterSpacing: '0.4px',
        }}
      >
        FYI
      </div>
      <h2 style={{ margin: 0, fontSize: '22px', color: '#111827' }}>
        Not Applicable for Patient Accounts
      </h2>
      <p style={{ margin: '10px 0 0', fontSize: '14px', color: '#64748b', lineHeight: 1.6 }}>
        The Clinician View section is for medical professionals. Please use Patient Chat, Symptom Tracker, or Medication Tracker.
      </p>
    </div>
  );

  const renderNotifications = () => (
    <div
      style={{
        background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid #dbeafe',
        borderRadius: '16px',
        padding: '28px 22px',
        maxWidth: '720px',
        margin: '0 auto',
        textAlign: 'center',
        boxShadow: '0 16px 34px rgba(15, 23, 42, 0.08)',
      }}
    >
      <div style={{ fontSize: '28px', marginBottom: '8px' }}>Notifications</div>
      <p style={{ margin: 0, fontSize: '14px', color: '#64748b', lineHeight: 1.6 }}>
        Your care updates and reminders will appear here.
      </p>
    </div>
  );

  const mobileTopButtonStyle = (isActive = false) => ({
    border: isActive ? '1px solid #1D9E75' : '1px solid #d1d5db',
    background: isActive ? '#ecfdf5' : 'white',
    color: isActive ? '#065f46' : '#334155',
    borderRadius: '10px',
    padding: '7px 9px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '700',
  });

  return (
    <main
      style={{
        minHeight: '100vh',
        background: 'radial-gradient(circle at 0% 0%, #e0f2fe 0%, #f8fafc 38%), radial-gradient(circle at 100% 100%, #dcfce7 0%, #f8fafc 42%)',
        padding: '20px',
      }}
    >
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '14px',
            marginBottom: '18px',
            background: 'rgba(255, 255, 255, 0.82)',
            border: '1px solid rgba(209, 213, 219, 0.75)',
            borderRadius: '16px',
            padding: isMobile ? '10px 10px' : '14px 16px',
            backdropFilter: 'blur(6px)',
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
            position: isMobile ? 'sticky' : 'static',
            top: isMobile ? '8px' : 'auto',
            zIndex: isMobile ? 1200 : 'auto',
          }}
        >
          {isMobile ? (
            <div style={{ width: '100%' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => setActiveView('notifications')}
                  style={{ ...mobileTopButtonStyle(activeView === 'notifications'), justifySelf: 'start' }}
                >
                  Notifications
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <h1 
                    style={{ margin: 0, fontSize: '22px', letterSpacing: '-0.2px', color: '#0f172a', cursor: 'pointer' }}
                    onClick={handleLogoClick}
                    title={isDeveloperMode ? "Developer Mode Active - Click to deactivate" : "Click 5 times to activate developer mode"}
                  >
                    Reaxano
                  </h1>
                  {isDeveloperMode && (
                    <span 
                      style={{ 
                        fontSize: '8px', 
                        color: '#1D9E75', 
                        fontWeight: 'bold',
                        background: '#ecfdf5',
                        padding: '1px 4px',
                        borderRadius: '3px',
                        border: '1px solid #1D9E75'
                      }}
                      title="Developer Mode Active"
                    >
                      DEV
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={onEditProfile}
                  style={{ ...mobileTopButtonStyle(false), justifySelf: 'end' }}
                >
                  Account Details
                </button>
              </div>
              <div style={{ marginTop: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <p style={{ margin: 0, color: '#64748b', fontSize: '12px' }}>
                  Signed in as {user?.name || user?.username || 'User'}
                </p>
                <button
                  type="button"
                  onClick={onLogout}
                  style={{
                    border: '1px solid #fecaca',
                    background: '#fff1f2',
                    color: '#9f1239',
                    borderRadius: '9px',
                    padding: '6px 9px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '700',
                  }}
                >
                  Log Out
                </button>
              </div>
            </div>
          ) : (
            <>
              <div style={{ minWidth: '220px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <h1 
                    style={{ margin: 0, fontSize: '24px', letterSpacing: '-0.2px', color: '#0f172a', cursor: 'pointer' }}
                    onClick={handleLogoClick}
                    title={isDeveloperMode ? "Developer Mode Active - Click to deactivate" : "Click 5 times to activate developer mode"}
                  >
                    Reaxano
                  </h1>
                  {isDeveloperMode && (
                    <span 
                      style={{ 
                        fontSize: '10px', 
                        color: '#1D9E75', 
                        fontWeight: 'bold',
                        background: '#ecfdf5',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        border: '1px solid #1D9E75'
                      }}
                      title="Developer Mode Active"
                    >
                      DEV
                    </span>
                  )}
                </div>
                <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '13px' }}>
                  Signed in as {user?.name || user?.username || 'User'}
                </p>
              </div>

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setActiveView('lending')}
                  style={tabButtonStyle('lending')}
                >
                  Equipment Lending
                </button>
                {patientChatEnabled && (
                  <button
                    type="button"
                    onClick={() => setActiveView('patient')}
                    style={tabButtonStyle('patient')}
                  >
                    Patient Chat
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setActiveView('symptoms')}
                  style={tabButtonStyle('symptoms')}
                >
                  Symptom Tracker
                </button>
                <button
                  type="button"
                  onClick={() => setActiveView('medications')}
                  style={tabButtonStyle('medications')}
                >
                  Medication Tracker
                </button>
                <button
                  type="button"
                  onClick={() => setActiveView('trials')}
                  style={tabButtonStyle('trials')}
                >
                  Clinical Trials
                </button>
                {drugMonographsEnabled && (
                  <button
                    type="button"
                    onClick={() => setActiveView('monographs')}
                    style={tabButtonStyle('monographs')}
                  >
                    Drug Monographs
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setActiveView('peerSupport')}
                  style={tabButtonStyle('peerSupport')}
                >
                  Peer Support
                </button>
                {isDeveloperMode && (
                  <button
                    type="button"
                    onClick={() => setActiveView('clinician')}
                    style={tabButtonStyle('clinician')}
                  >
                    Clinician View
                  </button>
                )}
                {isDeveloperMode && (
                  <>
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <label style={{ fontSize: '12px', color: '#64748b' }}>Patient Chat:</label>
                      <button
                        type="button"
                        onClick={() => setPatientChatEnabled(!patientChatEnabled)}
                        style={{
                          border: '1px solid #d1d5db',
                          background: patientChatEnabled ? '#1D9E75' : '#fff',
                          color: patientChatEnabled ? 'white' : '#334155',
                          borderRadius: '6px',
                          padding: '4px 8px',
                          cursor: 'pointer',
                          fontSize: '11px',
                          fontWeight: '600',
                        }}
                      >
                        {patientChatEnabled ? 'ON' : 'OFF'}
                      </button>
                    </div>
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <label style={{ fontSize: '12px', color: '#64748b' }}>Drug Monographs:</label>
                      <button
                        type="button"
                        onClick={() => setDrugMonographsEnabled(!drugMonographsEnabled)}
                        style={{
                          border: '1px solid #d1d5db',
                          background: drugMonographsEnabled ? '#1D9E75' : '#fff',
                          color: drugMonographsEnabled ? 'white' : '#334155',
                          borderRadius: '6px',
                          padding: '4px 8px',
                          cursor: 'pointer',
                          fontSize: '11px',
                          fontWeight: '600',
                        }}
                      >
                        {drugMonographsEnabled ? 'ON' : 'OFF'}
                      </button>
                    </div>
                  </>
                )}
                <button
                  type="button"
                  onClick={onEditProfile}
                  style={{
                    border: '1px solid #d1d5db',
                    background: '#fff',
                    borderRadius: '10px',
                    padding: '9px 12px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#334155',
                    boxShadow: '0 2px 6px rgba(15, 23, 42, 0.05)',
                  }}
                >
                  Edit Profile
                </button>
                <button
                  type="button"
                  onClick={onLogout}
                  style={{
                    border: '1px solid #fecaca',
                    background: '#fff1f2',
                    color: '#9f1239',
                    borderRadius: '10px',
                    padding: '9px 12px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '700',
                  }}
                >
                  Log Out
                </button>
              </div>
            </>
          )}
        </header>

        <div style={{ paddingBottom: isMobile ? '84px' : 0 }}>
        {activeView === 'notifications' ? (
          renderNotifications()
        ) : activeView === 'lending' ? (
          <EquipmentLendingLibrary />
        ) : activeView === 'clinician' ? (
          isClinician
            ? <ClinicianDashboard user={user} userProfile={userProfile} />
            : renderClinicianOnlyMessage()
        ) : activeView === 'medications' ? (
          isClinician
            ? renderPatientOnlyMessage('Medication Tracker')
            : <MedicationTracker user={user} onOpenChat={handleOpenChat} />
        ) : activeView === 'trials' ? (
          isClinician
            ? renderPatientOnlyMessage('Clinical Trials')
            : <ClinicalTrials user={user} userProfile={userProfile} onOpenChat={handleOpenChat} />
        ) : activeView === 'symptoms' ? (
          isClinician
            ? renderPatientOnlyMessage('Symptom Tracker')
            : <SymptomTracker user={user} onOpenChat={handleOpenChat} />
        ) : activeView === 'monographs' ? (
          drugMonographsEnabled ? (
            <DrugMonographs user={user} userProfile={userProfile} />
          ) : (
            <div style={{
              background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid #dbeafe',
              borderRadius: '16px',
              padding: '30px 24px',
              maxWidth: '720px',
              margin: '0 auto',
              textAlign: 'center',
              boxShadow: '0 16px 34px rgba(15, 23, 42, 0.08)',
            }}>
              <h2 style={{ margin: 0, fontSize: '22px', color: '#111827' }}>
                Drug Monographs Disabled
              </h2>
              <p style={{ margin: '10px 0 0', fontSize: '14px', color: '#64748b', lineHeight: 1.6 }}>
                The Drug Monographs feature has been turned off. You can enable it using the toggle above.
              </p>
            </div>
          )
        ) : activeView === 'peerSupport' ? (
          <PeerSupport />
        ) : (
          patientChatEnabled ? (
            isClinician
              ? renderPatientOnlyMessage('Patient Chat')
              : <PatientChat
                  userProfile={{
                    ...userProfile,
                    username: user?.username || userProfile?.username,
                  }}
                />
          ) : (
            <div style={{
              background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid #dbeafe',
              borderRadius: '16px',
              padding: '30px 24px',
              maxWidth: '720px',
              margin: '0 auto',
              textAlign: 'center',
              boxShadow: '0 16px 34px rgba(15, 23, 42, 0.08)',
            }}>
              <h2 style={{ margin: 0, fontSize: '22px', color: '#111827' }}>
                Patient Chat Disabled
              </h2>
              <p style={{ margin: '10px 0 0', fontSize: '14px', color: '#64748b', lineHeight: 1.6 }}>
                The Patient Chat feature has been turned off. You can enable it using the toggle above.
              </p>
            </div>
          )
        )}
        </div>

        {isMobile && (
          <nav
            style={{
              position: 'fixed',
              left: '10px',
              right: '10px',
              bottom: '10px',
              zIndex: 1300,
              background: 'rgba(255, 255, 255, 0.96)',
              backdropFilter: 'blur(8px)',
              border: '1px solid #d1d5db',
              borderRadius: '14px',
              boxShadow: '0 -8px 20px rgba(15, 23, 42, 0.08)',
              padding: '8px 8px calc(10px + env(safe-area-inset-bottom))',
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(44px, 1fr))', gap: '6px' }}>
              <button aria-label="Equipment Lending" title="Equipment Lending" type="button" onClick={() => setActiveView('lending')} style={mobileTabButtonStyle(activeView === 'lending')}>🧰</button>
              {patientChatEnabled && (
                <button aria-label="Patient Chat" title="Patient Chat" type="button" onClick={() => setActiveView('patient')} style={mobileTabButtonStyle(activeView === 'patient')}>💬</button>
              )}
              <button aria-label="Symptom Tracker" title="Symptom Tracker" type="button" onClick={() => setActiveView('symptoms')} style={mobileTabButtonStyle(activeView === 'symptoms')}>🩺</button>
              <button aria-label="Medication Tracker" title="Medication Tracker" type="button" onClick={() => setActiveView('medications')} style={mobileTabButtonStyle(activeView === 'medications')}>💊</button>
              <button aria-label="Clinical Trials" title="Clinical Trials" type="button" onClick={() => setActiveView('trials')} style={mobileTabButtonStyle(activeView === 'trials')}>🧪</button>
              {drugMonographsEnabled && (
                <button aria-label="Drug Monographs" title="Drug Monographs" type="button" onClick={() => setActiveView('monographs')} style={mobileTabButtonStyle(activeView === 'monographs')}>📖</button>
              )}
              <button aria-label="Peer Support" title="Peer Support" type="button" onClick={() => setActiveView('peerSupport')} style={mobileTabButtonStyle(activeView === 'peerSupport')}>🤝</button>
              {isDeveloperMode && (
                <button aria-label="Clinician View" title="Clinician View" type="button" onClick={() => setActiveView('clinician')} style={mobileTabButtonStyle(activeView === 'clinician')}>⚕️</button>
              )}
            </div>
          </nav>
        )}
      </div>
    </main>
  );
}

const mobileTabButtonStyle = (isActive) => ({
  border: isActive ? '1px solid #384477' : '1px solid #d1d5db',
  background: isActive ? '#1a2763' : 'white',
  color: isActive ? '#121b49' : '#334155',
  borderRadius: '10px',
  minHeight: '44px',
  padding: '8px 4px',
  fontSize: '22px',
  lineHeight: 1,
  fontWeight: isActive ? '700' : '500',
  cursor: 'pointer',
});
